// src/ui/actionBeatMotion.js
//
// Single source of truth for the "action beats" system (WAVE 1 · ENGINE,
// 2026-09-24): shared sizing/timing/easing constants for the confirm-modal
// action beat safety machine (useModalActionBeat + ModalActionBeat). The one
// visual is FILL (the tapped button itself fills, KAN-1078); the RING constants
// below are legacy timing only, nothing draws a ring any more.
//
// Pure constants only — no JSX, no side effects. Components import from here
// so timing/geometry can change in one place without touching every consumer.

/**
 * Set on the beat visual while a confirm is committing (working → success):
 * ModalActionBeat, ActionBeatFill (phase !== 'idle') and the shared beat visuals. AnimatedModalShell watches for it to bring a touch card
 * that RESTS under the header back to the centre (useModalRestRelease, KAN-930).
 */
export const ACTION_BEAT_ATTR = 'data-cvx-action-beat';

/** Which visual a confirm declares. */
export const BEAT_KIND = {
  RING: 'ring',
  FILL: 'fill',
};

/** RING geometry, in px unless noted (mockup "Placement rules"). */
export const RING = {
  size: 88,
  stroke: 5,
  radius: 40,
  avatar: 60,
  avatarInset: 14,
  badge: 24,
  badgeRim: 3,
  badgeOffset: 60,
  gapName: 14,
  gapStatus: 4,
  gapPill: 12,
  nameFontPx: 17,
  statusFontPx: 13.5,
};

/** Circumference of the progress ring's r=40 circle (2*pi*r). */
export const RING_CIRCUMFERENCE = 2 * Math.PI * RING.radius;

/** RING timing, in ms (mockup "The ring, beat by beat"). */
export const RING_MS = {
  dissolve: 120,
  fly: 320,
  height: 280,
  trackFadeDelay: 220,
  trackFade: 200,
  runDelay: 300,
  rise: 240,
  riseDelays: [220, 260, 300],
  sweep: 1200,
  sweepTo: 0.85,
  crawlAfter: 1300,
  crawlTo: 0.95,
  crawl: 8000,
  complete: 240,
  haloDelay: 120,
  halo: 640,
  badgeDelay: 140,
  badge: 320,
  tickDelay: 260,
  tick: 300,
  statusDelay: 160,
  pillDelay: 240,
  hold: 900,
  minWorking: 1100,
};

/** FILL timing, in ms (mockup "FILL (A)" CSS). */
export const FILL_MS = {
  fold: 260,
  sweep: 1200,
  sweepTo: 0.85,
  crawlAfter: 1300,
  crawlTo: 0.95,
  crawl: 8000,
  complete: 180,
  labelSwap: 160,
  tickDelay: 80,
  tick: 320,
  hold: 650,
  minWorking: 450,
};

/**
 * The touch height of every action button that can play a beat (KAN-768). A
 * button keeps ONE height through idle -> working -> success: the idle
 * footers and ActionBeatFill both take this floor on phone/tablet, so the
 * swap between them never moves the row (drawer footers were 36px idle and
 * 44px once the beat began).
 */
export const ACTION_BOX_TOUCH_MIN_HEIGHT = 44;

/** `{ minHeight: 44 }` on touch, nothing on desktop — spread into an idle footer button. */
export function touchActionBox(touch) {
  return touch ? { minHeight: ACTION_BOX_TOUCH_MIN_HEIGHT } : null;
}

/**
 * The touch floor as a FLOOR: `{ minHeight: max(callerMinHeight, 44) }` on touch,
 * nothing on desktop. A caller that asked for more (Log Job's 48px phone submit)
 * keeps it; a smaller one (a 36px drawer button) is lifted to 44. A caller value
 * that is not plain px (calc, %, rem) is left alone -- it is the caller's call.
 */
export function touchActionBoxFloor(touch, callerMinHeight) {
  if (!touch) return null;
  if (callerMinHeight == null || callerMinHeight === '') return { minHeight: ACTION_BOX_TOUCH_MIN_HEIGHT };
  if (typeof callerMinHeight === 'number') return { minHeight: Math.max(callerMinHeight, ACTION_BOX_TOUCH_MIN_HEIGHT) };
  const m = /^(\d+(?:\.\d+)?)px$/.exec(String(callerMinHeight).trim());
  if (!m) return { minHeight: callerMinHeight };
  return { minHeight: Math.max(Number(m[1]), ACTION_BOX_TOUCH_MIN_HEIGHT) };
}

/** Shared easing curves. */
export const BEAT_EASE = {
  out: 'cubic-bezier(0.16, 1, 0.3, 1)',
  sweep: 'cubic-bezier(0.22, 1, 0.36, 1)',
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
};

/**
 * The `minWorkingMs` / `holdMs` pair a confirm's `useModalActionBeat` call
 * should pass for a given beat kind, so every ring confirm and every fill
 * confirm shares one timing decision instead of each consumer picking its
 * own numbers. Unknown/legacy kinds return `{}` so a caller can always
 * `useModalActionBeat({ ...beatTiming(kind) })` — spreading nothing keeps
 * today's hook defaults (0 / ACTION_BEAT_SUCCESS_HOLD_MS) intact.
 */
export function beatTiming(kind) {
  if (kind === BEAT_KIND.RING) return { minWorkingMs: RING_MS.minWorking, holdMs: RING_MS.hold };
  if (kind === BEAT_KIND.FILL) return { minWorkingMs: FILL_MS.minWorking, holdMs: FILL_MS.hold };
  return {};
}
